import { useMemo, useState } from 'react';
import type { ChartDataPoint } from '../api/stressTestApi';

interface SensitivityChartProps {
    data: ChartDataPoint[];
    baselineKey: keyof ChartDataPoint;
    actualKey: keyof ChartDataPoint;
    title: string;
    description: string;
    idealLabel: string;
    actualLabel: string;
    dragLabel: string;
    dragColor?: 'red' | 'orange';
    currency?: string;
}

const VIEW_W = 600;
const VIEW_H = 260;
const PADDING_L = 60;
const PADDING_R = 20;
const PADDING_T = 20;
const PADDING_B = 30;
const PLOT_W = VIEW_W - PADDING_L - PADDING_R;
const PLOT_H = VIEW_H - PADDING_T - PADDING_B;

function formatCurrency(value: number, currency: string): string {
    if (Math.abs(value) >= 1_000_000) return `${currency}${(value / 1_000_000).toFixed(2)}M`;
    if (Math.abs(value) >= 1_000) return `${currency}${(value / 1_000).toFixed(1)}k`;
    return `${currency}${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export const SensitivityChart = ({
    data,
    baselineKey,
    actualKey,
    title,
    description,
    idealLabel,
    actualLabel,
    dragLabel,
    dragColor = 'red',
    currency = '$',
}: SensitivityChartProps) => {
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);

    const { minVal, maxVal, maxYear } = useMemo(() => {
        if (!data || data.length === 0) return { minVal: 0, maxVal: 1, maxYear: 1 };

        const allVals = data.flatMap((d) => [Number(d[baselineKey] || 0), Number(d[actualKey] || 0)]);
        const max = Math.max(...allVals, 1);
        const min = Math.min(...allVals, 0);

        return {
            minVal: min,
            maxVal: max + (max - min) * 0.1 || max * 1.1,
            maxYear: Math.max(...data.map(d => d.year), 1)
        };
    }, [data, baselineKey, actualKey]);

    const getX = (year: number) => PADDING_L + (year / maxYear) * PLOT_W;
    const getY = (val: number) => {
        const range = maxVal - minVal;
        const norm = range === 0 ? 0 : (val - minVal) / range;
        return PADDING_T + PLOT_H - norm * PLOT_H;
    };

    const { baselinePath, actualPath, gapPath } = useMemo(() => {
        if (!data || data.length < 2) return { baselinePath: '', actualPath: '', gapPath: '' };

        const bPath = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${getX(d.year)} ${getY(Number(d[baselineKey] || 0))}`).join(' ');
        const aPath = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${getX(d.year)} ${getY(Number(d[actualKey] || 0))}`).join(' ');

        // Gap area polygon: Path forward along ideal, then backward along actual to close shape
        const bPathForward = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${getX(d.year)} ${getY(Number(d[baselineKey] || 0))}`).join(' ');
        const aPathReverse = [...data].reverse().map(d => `L ${getX(d.year)} ${getY(Number(d[actualKey] || 0))}`).join(' ');
        const gap = `${bPathForward} ${aPathReverse} Z`;

        return { baselinePath: bPath, actualPath: aPath, gapPath: gap };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, baselineKey, actualKey, minVal, maxVal, maxYear]);

    if (!data || data.length < 2) return null;

    const dragColorHex = dragColor === 'orange' ? '#f97316' : '#ef4444';

    return (
        <div className="flex flex-col h-full w-full">
            <div className="mb-2">
                <h4 className="text-sm font-bold text-slate-900 dark:text-white">{title}</h4>
                <p className="text-[11px] muted mt-0.5 leading-relaxed">{description}</p>
            </div>

            <div className="relative w-full flex-1 min-h-[200px] group select-none">
                <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} className="w-full h-full overflow-visible touch-none" preserveAspectRatio="xMidYMid meet" onMouseLeave={() => setHoverIndex(null)}>
                    <defs>
                        <pattern id={`hatch-${dragColor}`} patternUnits="userSpaceOnUse" width="8" height="8">
                            <path d="M-2,2 l4,-4 M0,8 l8,-8 M6,10 l4,-4" stroke={dragColorHex} strokeWidth="1" strokeOpacity="0.3"/>
                        </pattern>
                    </defs>

                    {/* Gridlines */}
                    {[0, 0.5, 1].map(tick => {
                        const y = PADDING_T + PLOT_H * (1 - tick);
                        return (
                            <g key={`y-${tick}`}>
                                <line x1={PADDING_L} x2={VIEW_W - PADDING_R} y1={y} y2={y} stroke="currentColor" strokeOpacity={0.1} strokeDasharray="3 3"/>
                                <text x={PADDING_L - 8} y={y + 4} textAnchor="end" fontSize="10" className="fill-slate-400 font-medium">{formatCurrency(minVal + (maxVal - minVal) * tick, currency)}</text>
                            </g>
                        );
                    })}

                    {/* Drag Gap Area (Hatched) */}
                    <path d={gapPath} fill={`url(#hatch-${dragColor})`} className="transition-all duration-300"/>

                    {/* Actual Line */}
                    <path d={actualPath} fill="none" stroke="#10b981" strokeWidth="2.5" className="transition-all"/>

                    {/* Ideal Line (No fee / No tax) */}
                    <path d={baselinePath} fill="none" stroke="#64748b" strokeWidth="2" strokeDasharray="6 6" className="dark:stroke-slate-400 transition-all"/>

                    {/* Baseline Axis */}
                    <line x1={PADDING_L} x2={VIEW_W - PADDING_R} y1={PADDING_T + PLOT_H} y2={PADDING_T + PLOT_H} stroke="currentColor" strokeOpacity={0.4} strokeWidth="1.5"/>

                    {/* Hover Interactions */}
                    {data.map((point, i) => {
                        const cx = getX(point.year);
                        const vBase = Number(point[baselineKey] || 0);
                        const vActual = Number(point[actualKey] || 0);
                        const dragAmount = vBase - vActual;

                        const prevX = i > 0 ? getX(data[i - 1].year) : PADDING_L;
                        const nextX = i < data.length - 1 ? getX(data[i + 1].year) : VIEW_W - PADDING_R;
                        const isHovered = hoverIndex === i;

                        return (
                            <g key={`zone-${i}`}>
                                <rect x={(prevX + cx)/2} y={PADDING_T} width={(cx + nextX)/2 - (prevX + cx)/2} height={PLOT_H} fill="transparent" onMouseEnter={() => setHoverIndex(i)} className="cursor-crosshair outline-none"/>

                                {isHovered && (
                                    <g className="pointer-events-none animate-fade-in">
                                        <line x1={cx} x2={cx} y1={PADDING_T} y2={PADDING_T + PLOT_H} stroke={dragColorHex} strokeDasharray="4 4" strokeOpacity={0.7}/>
                                        <circle cx={cx} cy={getY(vBase)} r="4" fill="#64748b" stroke="#fff" strokeWidth="1.5"/>
                                        <circle cx={cx} cy={getY(vActual)} r="4" fill="#10b981" stroke="#fff" strokeWidth="1.5"/>

                                        {/* Tooltip Box */}
                                        <rect x={cx > VIEW_W / 2 ? cx - 145 : cx + 15} y={Math.max(PADDING_T, getY(vBase) - 20)} width="130" height="72" rx="6" className="fill-slate-800 dark:fill-slate-100 shadow-md"/>
                                        <text x={cx > VIEW_W / 2 ? cx - 135 : cx + 25} y={Math.max(PADDING_T, getY(vBase) - 20) + 16} fontSize="9" fontWeight="bold" className="fill-slate-400 dark:fill-slate-500 uppercase tracking-wide">Year {point.year}</text>
                                        <text x={cx > VIEW_W / 2 ? cx - 135 : cx + 25} y={Math.max(PADDING_T, getY(vBase) - 20) + 32} fontSize="11" fontWeight="bold" className="fill-slate-300 dark:fill-slate-700">{idealLabel}: {formatCurrency(vBase, currency)}</text>
                                        <text x={cx > VIEW_W / 2 ? cx - 135 : cx + 25} y={Math.max(PADDING_T, getY(vBase) - 20) + 48} fontSize="11" fontWeight="bold" className="fill-emerald-400 dark:fill-emerald-600">{actualLabel}: {formatCurrency(vActual, currency)}</text>
                                        <text x={cx > VIEW_W / 2 ? cx - 135 : cx + 25} y={Math.max(PADDING_T, getY(vBase) - 20) + 64} fontSize="11" fontWeight="bold" fill={dragColorHex}>
                                            {dragLabel}: {formatCurrency(dragAmount, currency)}
                                        </text>
                                    </g>
                                )}
                            </g>
                        );
                    })}
                </svg>

                {/* Legend */}
                <div className="absolute top-0 right-0 flex gap-3 text-[9px] font-bold uppercase tracking-wide muted">
                    <div className="flex items-center gap-1.5"><span className="h-1 w-3 border-t-2 border-dashed border-slate-400"/> {idealLabel}</div>
                    <div className="flex items-center gap-1.5"><span className="h-1 w-3 bg-emerald-500"/> {actualLabel}</div>
                    <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm opacity-40" style={{backgroundColor: dragColorHex}}/> Drag</div>
                </div>
            </div>
        </div>
    );
};
